import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Container, NbCard, Chip } from "@/components/nb";
import MilestoneWall from "@/components/MilestoneWall";
import { Flame, Trophy, ArrowLeft, Sparkles } from "lucide-react";

export default function PublicProfile() {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setProfile(null); setErr(null);
    api.get(`/users/public/${userId}`)
      .then((r) => setProfile(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Profile not found"));
  }, [userId]);

  if (err) return (
    <Container>
      <NbCard className="!p-8">
        <h1 className="font-heading font-black text-3xl">Profile not found</h1>
        <p className="mt-2 text-neutral-700">{err}</p>
        <Link to="/" className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg nb-border nb-shadow nb-hover bg-black text-white font-heading font-bold text-sm uppercase">
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </NbCard>
    </Container>
  );

  if (!profile) return <Container><div className="font-mono-sl text-xs uppercase text-neutral-500">Loading…</div></Container>;

  const bg = profile.avatar_bg ? `bg-sl-${profile.avatar_bg}` : "bg-sl-primary";

  return (
    <Container>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <NbCard color="tertiary" className="!p-6" data-testid="public-profile-hero">
          <div className="flex items-center gap-4 flex-wrap">
            <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full ${bg} nb-border nb-shadow grid place-items-center text-4xl md:text-5xl`}>
              {profile.avatar_emoji || (profile.name?.[0] || "S").toUpperCase()}
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-sl text-[10px] uppercase text-neutral-600">PUBLIC PROFILE</div>
              <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter leading-none">{profile.name}</h1>
              {profile.grade && <Chip color="primary" className="mt-2">GRADE {profile.grade}</Chip>}
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 justify-end">
                <motion.span animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1.5, repeat: Infinity }}>
                  <Flame className="w-7 h-7 text-orange-500 fill-orange-300" />
                </motion.span>
                <span className="font-heading font-black text-4xl">{profile.streak?.current || 0}</span>
              </div>
              <div className="font-mono-sl text-[10px] uppercase text-neutral-600">day streak · longest {profile.streak?.longest || 0}d</div>
            </div>
          </div>
        </NbCard>

        <MilestoneWall streak={profile.streak} userName={profile.name} userId={profile.id} shareable={true} />

        <div className="mt-6" data-testid="public-trophies">
          <div className="flex items-center gap-2 mb-3"><Trophy className="w-5 h-5" /><h3 className="font-heading font-black text-2xl tracking-tight">Trophies · {profile.attempts} test{profile.attempts === 1 ? "" : "s"}</h3></div>
          {profile.trophies?.length === 0 && (
            <NbCard className="!p-4"><div className="text-neutral-700 text-sm">No trophies yet.</div></NbCard>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {profile.trophies?.map((t) => (
              <NbCard key={t.id} color="accent" className="!p-4">
                <div className="text-4xl">{t.emoji || "🏆"}</div>
                <div className="mt-2 font-heading font-bold text-base tracking-tight">{t.name}</div>
                <div className="font-mono-sl text-[10px] uppercase mt-1">EARNED · {t.count}× </div>
              </NbCard>
            ))}
          </div>
        </div>
      </motion.div>
    </Container>
  );
}
